import {compact} from '@benchling/js-utils';
import React from 'react';

import {AnalyticsEventOutcome} from 'analytics/types/AnalyticsEventOutcome';
import BenchlingConfig from 'BenchlingConfig';
import ChooseImportLocationModalPane from 'bulk-import/panes/ChooseImportLocationModalPane';
import {SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import ModelTypeConstants, {
  BioentityType,
  SEQUENCE_SUBTYPES,
  SequenceSubtype,
} from 'constants/ModelTypeConstants';
import {
  OligoAwareFolderItemType,
  SchemalessBulkImportSupportedOligoAwareTypes,
} from 'constants/RegistryConstants';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {canSchemaHaveMods} from 'molbio/OligoUtil';
import {
  trackSubmitInBioentitySpreadsheetImportModal,
  trackUploadSpreadsheetInNucleotideImportModal,
} from 'molbio/SequenceAnalyticsHelper';
import * as SeqUtil from 'molbio/SeqUtil';
import {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {withLoadingScreen} from 'util/Promises';
import {Optional} from 'util/Types';

import ImportFilesTutorialLink from './ImportFilesTutorialLink';
import {getEnabledBioentityImportOptions} from './panes/BioentityImportOptions';
import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
  DerivedColumnsStoreKeys,
  IMPORT_TYPE,
  ImportSourceType,
  MOLECULE_COLUMN_TYPES,
  OPTIONS,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_COLUMN_TYPES,
  TaskState,
  TRANSFER_MEASUREMENT_TYPES,
  VALUE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';
import * as StorableColumnsUtil from './storables/StorableColumnsUtil';
import * as TransferColumnsUtil from './TransferColumnsUtil';

const OLIGO_IMPORT_EXAMPLE_HEADER = 'Name\tBases\n';

const REQUIRED_COLUMN_TYPES = [COLUMN_TYPE.FILENAME];

interface Props {
  initialFolder?: SelectedDestination | null;
  onRequestClose: () => void;
  onFolderChange?: (newFolderId: string | null) => void;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
  title: string;
  isSchemaRequired: boolean;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  shouldFilterToComplexPolymerSchemas?: boolean;
}

function entityTypeIsNucleotideSequence(
  entityType?: OligoAwareFolderItemType | null
): entityType is SequenceSubtype {
  return entityType != null && SEQUENCE_SUBTYPES.includes(entityType);
}

class BulkCreateInventoryFilesFlow extends React.Component<Props> {
  static defaultProps = {
    onRequestClose: SpreadsheetBulkImportActions.closeModal,
    title: 'Import entities from spreadsheet',
    isSchemaRequired: true,
  };

  static async open(props: Partial<Props>): Promise<void> {
    await withLoadingScreen(doQuerySafe<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY}));
    LegacyModalContainer.open(SPREADSHEET_BULK_IMPORT_KEY, <BulkCreateInventoryFilesFlow {...props} />);
  }

  // Show the schema selector if we are not creating a schemaless entity, OR if we are creating a schemaless
  // entity that allows the optional selection of a schema, such as a molecule
  _shouldShowSchemaSelector = (): boolean =>
    !this.props.schemalessCreationEntityType || !this.props.isSchemaRequired;

  _shouldHaveResidues = (selectedSchemaIds: SelectedSchemaIds): boolean =>
    (selectedSchemaIds.tagSchemaId != null &&
      SpreadsheetBulkImportUtil.shouldHaveResidues(selectedSchemaIds)) ||
    TagSchemaUtil.entityTypeSupportsResidues(this.props.schemalessCreationEntityType);

  _canHaveMods = (
    selectedSchemaIds: SelectedSchemaIds,
    schemalessCreationEntityType: Optional<SchemalessBulkImportSupportedOligoAwareTypes>
  ): boolean => {
    if (selectedSchemaIds.tagSchemaId != null) {
      const tagSchema = SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds);
      if (tagSchema) {
        return canSchemaHaveMods(tagSchema);
      }
    }
    return (
      BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES &&
      schemalessCreationEntityType != null &&
      SeqUtil.subtypeSupportsChemicalMods(schemalessCreationEntityType)
    );
  };

  _shouldHaveChemicalStructure(
    selectedSchemaIds: SelectedSchemaIds,
    schemalessCreationEntityType: Optional<SchemalessBulkImportSupportedOligoAwareTypes>
  ): boolean {
    return (
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE &&
      ((selectedSchemaIds.tagSchemaId != null &&
        SpreadsheetBulkImportUtil.shouldHaveChemicalStructure(selectedSchemaIds)) ||
        schemalessCreationEntityType === ModelTypeConstants.MOLECULE)
    );
  }

  _chemicalStructureColumns(
    selectedSchemaIds: SelectedSchemaIds,
    schemalessCreationEntityType: Optional<SchemalessBulkImportSupportedOligoAwareTypes>
  ): Array<COLUMN_TYPE> {
    return this._shouldHaveChemicalStructure(selectedSchemaIds, schemalessCreationEntityType)
      ? MOLECULE_COLUMN_TYPES
      : [];
  }

  _getPanes: () => Array<Pane> = () =>
    compact([
      {
        paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_DESTINATION,
        configuration: ChooseImportLocationModalPane.configure({
          showRegistries: false,
          showInventory: true,
          shouldShowSchemaSelector: this._shouldShowSchemaSelector(),
          getEnabledOptions: getEnabledBioentityImportOptions,
          onDestinationFolderChange: this.props.onFolderChange,
          folderSelectorLabel: 'Project folder',
          shouldFilterToComplexPolymerSchemas: this.props.shouldFilterToComplexPolymerSchemas ?? false,
        }),
      },
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure({
          getExampleImageURL: ({selectedSchemaIds}) => {
            if (!this._shouldShowSchemaSelector()) {
              // Avoid showing generic example image for types that do not require a schema
              return '';
            }
            // Show screenshots with 'Bases' if importing sequences ('Amino Acids', proteins).
            const tagSchema = SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds);
            return SpreadsheetBulkImportUtil.getExampleImageURLForTagSchema(tagSchema);
          },
          onUploadSuccessAnalyticsCallback: (
            entityType: OligoAwareFolderItemType | null | undefined,
            uploadType: ImportSourceType
          ) => {
            if (entityTypeIsNucleotideSequence(entityType)) {
              trackUploadSpreadsheetInNucleotideImportModal(
                entityType,
                uploadType,
                AnalyticsEventOutcome.SUCCESS
              );
            }
          },

          onUploadFailAnalyticsCallback: (
            entityType: OligoAwareFolderItemType | null | undefined,
            uploadType: ImportSourceType,
            errorMessage: string
          ) => {
            if (entityTypeIsNucleotideSequence(entityType)) {
              trackUploadSpreadsheetInNucleotideImportModal(
                entityType,
                uploadType,
                AnalyticsEventOutcome.FAILURE,
                errorMessage
              );
            }
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes: ({selectedSchemaIds, options, schemalessCreationEntityType}) => {
            if (!this._shouldShowSchemaSelector()) {
              // Only show relevant fields when type does not require schema
              return compact([
                ...REQUIRED_COLUMN_TYPES,
                ...SpreadsheetBulkImportUtil.getPolymerColumnTypes(
                  this._shouldHaveResidues(selectedSchemaIds),
                  this._canHaveMods(selectedSchemaIds, schemalessCreationEntityType)
                ),
                ...this._chemicalStructureColumns(selectedSchemaIds, schemalessCreationEntityType),
                COLUMN_TYPE.DESCRIPTION,
                COLUMN_TYPE.IGNORE,
              ]);
            }
            const containerColumnTypes = options[OPTIONS.IMPORT_CONTAINERS]
              ? [
                  ...STORABLE_COLUMN_TYPES,
                  COLUMN_TYPE.NUMBER_OF_CONTAINERS,
                  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
                  ...TRANSFER_MEASUREMENT_TYPES,
                  ...StorableColumnsUtil.getSampleControlRolesColumns(),
                ]
              : [];
            return compact([
              ...REQUIRED_COLUMN_TYPES,
              ...SpreadsheetBulkImportUtil.getPolymerColumnTypes(
                this._shouldHaveResidues(selectedSchemaIds),
                this._canHaveMods(selectedSchemaIds, schemalessCreationEntityType)
              ),
              ...this._chemicalStructureColumns(selectedSchemaIds, schemalessCreationEntityType),
              ...SpreadsheetBulkImportUtil.getComplexPolymerColumnTypes(
                selectedSchemaIds,
                this.props.complexPolymerSchemaConfigurations
              ),
              ...VALUE_COLUMN_TYPES,
              ...containerColumnTypes,
            ]);
          },
          getRequiredColumnsError: (
            columns: Array<SpreadsheetColumn>,
            {
              selectedSchemaIds,
              schemalessCreationEntityType,
            }: {
              selectedSchemaIds: SelectedSchemaIds;
              schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
            }
          ) => {
            // Assert unique and required columns
            const measurementColumnsError = TransferColumnsUtil.getMeasurementColumnsError(columns, false);
            if (measurementColumnsError) {
              return measurementColumnsError;
            }
            const basesModsColumnsError = SpreadsheetBulkImportUtil.getErrorForCreatingPolymer(
              columns,
              this._shouldHaveResidues(selectedSchemaIds),
              this._canHaveMods(selectedSchemaIds, schemalessCreationEntityType)
            );
            if (basesModsColumnsError) {
              return basesModsColumnsError;
            }
            const chemicalStructureError = SpreadsheetBulkImportUtil.getErrorForChemicalStructureColumns(
              columns,
              this._shouldHaveChemicalStructure(selectedSchemaIds, schemalessCreationEntityType),
              true
            );
            if (chemicalStructureError) {
              return chemicalStructureError;
            }
            return SpreadsheetBulkImportUtil.getRequiredFieldError(
              REQUIRED_COLUMN_TYPES,
              columns,
              selectedSchemaIds,
              IMPORT_TYPE.INVENTORY_FILES,
              schemalessCreationEntityType
            );
          },
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateInventoryFiles,
          customHandleGoToNextPaneFn: (paneProps) => {
            if (SpreadsheetBulkImportUtil.shouldCheckDerivedColumns(paneProps.fileData.columns)) {
              SpreadsheetBulkImportActions.findDerivedColumns();
              SpreadsheetBulkImportActions.goToNextPane();
            } else {
              paneProps.onValidateSpreadsheet();
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.SHOW_ERRORS);
            }
          },
          savedMappingsConfig: {
            enableSavedMappings: true,
            uploadFlow: UploadFlowType.BULK_CREATE_ENTITIES,
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_DERIVED_COLUMNS,
        configuration: {
          columnTransformConfigs: DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
          derivedColumnsKey: DerivedColumnsStoreKeys.BULK_CREATE,
          onUpdateDerivedColumns: SpreadsheetBulkImportActions.setDerivedColumns,
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateInventoryFiles,
        },
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          customHandleGoToPrevPaneFn: (paneProps) => {
            if (
              paneProps.derivedColumnsData.state === TaskState.COMPLETED &&
              paneProps.derivedColumnsData.derivedColumns &&
              paneProps.derivedColumnsData.derivedColumns.length > 0
            ) {
              SpreadsheetBulkImportActions.goToPrevPane();
            } else {
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.MAP_COLUMN_NAMES);
            }
          },
          enableReplaceLinkWithText: true,
          enableSkipErrors: true,
          uploadFlow: UploadFlowType.BULK_CREATE_ENTITIES,
        }),
      },
    ]);

  override render(): JSX.Element {
    const isSchemalessOligoFlow =
      this.props.schemalessCreationEntityType != null &&
      (
        [
          ModelTypeConstants.DNA_OLIGO,
          ModelTypeConstants.RNA_OLIGO,
        ] as Array<SchemalessBulkImportSupportedOligoAwareTypes>
      ).includes(this.props.schemalessCreationEntityType);

    return (
      <SpreadsheetBulkImportModalContainer
        footer={this._shouldShowSchemaSelector() && <ImportFilesTutorialLink />}
        importType={IMPORT_TYPE.INVENTORY_FILES}
        initialSettings={{
          initialFolder: this.props.initialFolder,
          initialSchemalessCreationEntityType: this.props.schemalessCreationEntityType,
          initialRawText: isSchemalessOligoFlow ? OLIGO_IMPORT_EXAMPLE_HEADER : '',
        }}
        onImportFromSpreadsheet={() => {
          SpreadsheetBulkImportActions.createInventoryFilesFromSpreadsheetWithDataMods({
            onSubmitSpreadsheetCallback: (entityType: OligoAwareFolderItemType | null) => {
              trackSubmitInBioentitySpreadsheetImportModal(entityType as BioentityType);
            },
          });
        }}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title={this.props.title}
        isSchemaRequired={this.props.isSchemaRequired}
        complexPolymerSchemaConfigurations={this.props.complexPolymerSchemaConfigurations}
      />
    );
  }
}

export default BulkCreateInventoryFilesFlow;
